const TOP_RATED_MIN_SCORE = 4.5;
const TOP_RATED_MIN_REVIEWS = 5;
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
const VETERAN_TIERS = [5, 3, 1] as const;

export type BadgeTone = 'gold' | 'purple' | 'green' | 'blue';
export type BadgeIcon = 'star' | 'sparkles' | 'calendar' | 'ribbon';

export type ProfileBadge = {
  key: string;
  tone: BadgeTone;
  icon: BadgeIcon;
  label: string;
};

type Input = {
  score: number;
  reviewCount: number;
  insertedAt: string;
  acceptsBookings: boolean;
  subscriptionActive: boolean;
};

export const computeProfileBadges = ({
  score,
  reviewCount,
  insertedAt,
  acceptsBookings,
  subscriptionActive,
}: Input): ProfileBadge[] => {
  const badges: ProfileBadge[] = [];

  if (score >= TOP_RATED_MIN_SCORE && reviewCount >= TOP_RATED_MIN_REVIEWS) {
    badges.push({
      key: 'top-rated',
      tone: 'gold',
      icon: 'star',
      label: 'Top Rated',
    });
  }

  if (subscriptionActive) {
    badges.push({
      key: 'pro-member',
      tone: 'purple',
      icon: 'sparkles',
      label: 'Pro Member',
    });
  }

  if (acceptsBookings) {
    badges.push({
      key: 'open-for-bookings',
      tone: 'green',
      icon: 'calendar',
      label: 'Open for Bookings',
    });
  }

  const veteranYears = computeVeteranYears(insertedAt);
  if (veteranYears) {
    badges.push({
      key: `veteran-${veteranYears}`,
      tone: 'blue',
      icon: 'ribbon',
      label: `${veteranYears}-Year Member`,
    });
  }

  return badges;
};

const computeVeteranYears = (insertedAt: string): number | null => {
  if (!insertedAt) return null;
  const joined = new Date(insertedAt).getTime();
  if (Number.isNaN(joined)) return null;
  const yearsElapsed = (Date.now() - joined) / MS_PER_YEAR;
  for (const tier of VETERAN_TIERS) {
    if (yearsElapsed >= tier) return tier;
  }
  return null;
};
